import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  GoogleMap,
  useLoadScript,
  OverlayView,
} from "@react-google-maps/api";
import Navigation from "../app/components/NavigationSPA";
import type { AppDispatch, RootState } from "../app/redux/store";
import {
  getRodentProbes,
  getScrollInfoV2,
  updateRodentProbeFields,
  updateTrapCoordinates,
  selectRodentProbes,
  selectRodentLoading,
  selectScrollInfoTraps,
  selectUserInfo,
  selectCurrentTeam,
  type RodentProbe,
} from "../app/redux/slices/dataSlice";

// ── Types ─────────────────────────────────────────────────────────────────────

type DeviceFilter = "all" | "smartprobe" | "rodent";

interface NormalizedDevice {
  deviceType: "rodent" | "smartprobe";
  id: string;
  uniqueId: string;
  displayId: string;
  title: string;
  subtitle: string;
  latitude: number | null;
  longitude: number | null;
  isTriggered?: boolean;
  particleCount?: number;
  raw: RodentProbe | TrapDevice;
}

interface TrapDevice {
  trapId: string;
  trapName: string;
  location?: string;
  building?: string;
  batteryInfo?: string | null;
  wifiInfo?: string | null;
  particleCount?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  insectCollection?: {
    beetle?: number;
    moth?: number;
    weevil?: number;
    other?: number;
  } | null;
}

interface LatLng {
  lat: number;
  lng: number;
}

const MAP_LIBRARIES: ("places")[] = [];
const DEFAULT_CENTER: LatLng = { lat: 37.78825, lng: -122.4324 };
const DEFAULT_ZOOM = 13;
const CLOSE_ZOOM = 19;

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInsectCount(trap: TrapDevice): number {
  if (trap.insectCollection) {
    return (
      (trap.insectCollection.beetle ?? 0) +
      (trap.insectCollection.moth ?? 0) +
      (trap.insectCollection.weevil ?? 0) +
      (trap.insectCollection.other ?? 0)
    );
  }
  return trap.particleCount ?? 0;
}

function normalizeDevice(
  device: RodentProbe | TrapDevice,
  type: "rodent" | "smartprobe"
): NormalizedDevice {
  if (type === "rodent") {
    const r = device as RodentProbe;
    return {
      deviceType: "rodent",
      id: r._id || r.probeId,
      uniqueId: "rodent-" + (r._id || r.probeId),
      displayId: r.probeId,
      title: r.probeName || "ID: " + r.probeId,
      subtitle: r.location || "",
      latitude: r.latitude != null ? Number(r.latitude) : null,
      longitude: r.longitude != null ? Number(r.longitude) : null,
      isTriggered: !!r.probeTriggered,
      raw: r,
    };
  } else {
    const t = device as TrapDevice;
    const count = getInsectCount(t);
    return {
      deviceType: "smartprobe",
      id: t.trapId,
      uniqueId: "smartprobe-" + t.trapId,
      displayId: t.trapId,
      title: t.trapName || "ID: " + t.trapId,
      subtitle:
        "Building: " +
        (t.building || "Not Set") +
        " - Location: " +
        (t.location || "Not Set"),
      latitude: t.latitude != null ? Number(t.latitude) : null,
      longitude: t.longitude != null ? Number(t.longitude) : null,
      particleCount: count,
      raw: t,
    };
  }
}

function getMarkerColor(
  device: NormalizedDevice,
  yellow: number,
  lightRed: number,
  darkRed: number
): string {
  if (device.deviceType === "rodent") {
    return device.isTriggered ? "#FF4444" : "#4CAF50";
  }
  const count = device.particleCount ?? 0;
  if (count === 0 || count <= yellow) return "#4CAF50";
  if (count <= lightRed) return "#FFC107";
  if (count <= darkRed) return "#F44336";
  return "#D32F2F";
}

function truncateLabel(s: string, max = 15): string {
  return s.length > max ? s.slice(0, 12) + "..." : s;
}

function hasCoords(d: NormalizedDevice): boolean {
  return d.latitude != null && d.longitude != null;
}

// ── Custom Marker ─────────────────────────────────────────────────────────────

function CustomMarker({
  device,
  isSelected,
  color,
  onClick,
}: {
  device: NormalizedDevice;
  isSelected: boolean;
  color: string;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: "pointer",
        userSelect: "none",
        transform: `translate(-50%, -100%) scale(${hovered ? 1.08 : 1})`,
        transition: "transform 0.15s ease, opacity 0.15s ease",
        opacity: hovered ? 0.85 : 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
      }}
    >
      {/* Label box */}
      <div
        style={{
          background: "white",
          border: isSelected ? "2px solid #1d4ed8" : "1px solid #d1d5db",
          borderRadius: 6,
          padding: "3px 8px",
          fontSize: 11,
          fontWeight: 700,
          color: isSelected ? "#1d4ed8" : "#111827",
          whiteSpace: "nowrap",
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          maxWidth: 120,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {truncateLabel(device.title)}
      </div>
      {/* Circle */}
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: color,
          border: isSelected ? "2px solid #1d4ed8" : "2px solid white",
          boxShadow: isSelected
            ? "0 0 0 2px #1d4ed8, 0 2px 6px rgba(0,0,0,0.3)"
            : "0 2px 6px rgba(0,0,0,0.3)",
          flexShrink: 0,
        }}
      />
    </div>
  );
}

function NewMarker({ onClick }: { onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: "pointer",
        transform: `translate(-50%, -100%) scale(${hovered ? 1.08 : 1})`,
        transition: "transform 0.15s ease, opacity 0.15s ease",
        opacity: hovered ? 0.85 : 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
      }}
    >
      <div
        style={{
          background: "white",
          border: "2px solid #4169E1",
          borderRadius: 6,
          padding: "3px 8px",
          fontSize: 11,
          fontWeight: 700,
          color: "#4169E1",
          whiteSpace: "nowrap",
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
        }}
      >
        NEW
      </div>
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#4169E1",
          border: "2px solid white",
          boxShadow: "0 0 0 2px #4169E1, 0 2px 6px rgba(0,0,0,0.3)",
          flexShrink: 0,
        }}
      />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;

export default function UnifiedDeviceMapPage() {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentTeam = useSelector(selectCurrentTeam);
  const rodentProbes = useSelector(selectRodentProbes);
  const rodentLoading = useSelector(selectRodentLoading);
  const smartProbeTraps = useSelector(selectScrollInfoTraps);
  const smartProbeLoading = useSelector((s: RootState) => !s.data.traplistLoading);
  const userInfo = useSelector(selectUserInfo);

  const yellow = parseFloat(String(userInfo?.yellow ?? 0)) || 0;
  const lightRed = parseFloat(String(userInfo?.lightRed ?? 0)) || 0;
  const darkRed = parseFloat(String(userInfo?.darkRed ?? 0)) || 0;

  const [deviceFilter, setDeviceFilter] = useState<DeviceFilter>("all");
  const [selectedDevice, setSelectedDevice] = useState<NormalizedDevice | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedUnplacedDevice, setSelectedUnplacedDevice] = useState<NormalizedDevice | null>(null);
  const [newMarkerLocation, setNewMarkerLocation] = useState<LatLng | null>(null);
  const [mapCenter, setMapCenter] = useState<LatLng>(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [refreshing, setRefreshing] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const markerPressedRef = useRef(false);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const teamName = currentTeam?.name || "";

  // ── Fetch data on mount / team change ───────────────────────────────────────
  useEffect(() => {
    if (!teamName) return;
    dispatch(getRodentProbes({ teamName }));
    dispatch(getScrollInfoV2({ teamName, page: 1, pageSize: 100, filter: "id", ascend: true }));
    // Reset selection on team change
    setSelectedDevice(null);
    setIsEditMode(false);
    setIsAddMode(false);
    setSelectedUnplacedDevice(null);
    setNewMarkerLocation(null);
  }, [teamName, dispatch]);

  // ── Handle deep-link params ──────────────────────────────────────────────────
  useEffect(() => {
    const probeId = searchParams.get("selectedProbeId");
    const trapId = searchParams.get("selectedTrapId");
    if (probeId && rodentProbes) {
      const probe = rodentProbes.find((p) => p.probeId === probeId || p._id === probeId);
      if (probe) {
        const nd = normalizeDevice(probe, "rodent");
        setSelectedDevice(nd);
        setDeviceFilter("rodent");
        if (hasCoords(nd)) {
          setMapCenter({ lat: nd.latitude!, lng: nd.longitude! });
          setMapZoom(CLOSE_ZOOM);
        }
      }
    } else if (trapId && smartProbeTraps) {
      const trap = smartProbeTraps.find((t) => t.trapId === trapId);
      if (trap) {
        const nd = normalizeDevice(trap as TrapDevice, "smartprobe");
        setSelectedDevice(nd);
        setDeviceFilter("smartprobe");
        if (hasCoords(nd)) {
          setMapCenter({ lat: nd.latitude!, lng: nd.longitude! });
          setMapZoom(CLOSE_ZOOM);
        }
      }
    }
  }, [searchParams, rodentProbes, smartProbeTraps]);

  // ── Normalized device list ───────────────────────────────────────────────────
  const allDevices = useMemo<NormalizedDevice[]>(() => {
    const list: NormalizedDevice[] = [];
    if (deviceFilter !== "smartprobe" && rodentProbes) {
      rodentProbes.forEach((r) => list.push(normalizeDevice(r, "rodent")));
    }
    if (deviceFilter !== "rodent" && smartProbeTraps) {
      smartProbeTraps.forEach((t) => list.push(normalizeDevice(t as TrapDevice, "smartprobe")));
    }
    return list;
  }, [rodentProbes, smartProbeTraps, deviceFilter]);

  const validDevices = useMemo(() => allDevices.filter(hasCoords), [allDevices]);
  const unplacedDevices = useMemo(() => allDevices.filter((d) => !hasCoords(d)), [allDevices]);

  // ── Auto-fit map on first load ───────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || validDevices.length === 0) return;

    // If we navigated here via deep link, zoom to that specific device instead of fitting all
    const deepProbeId = searchParams.get("selectedProbeId");
    const deepTrapId = searchParams.get("selectedTrapId");
    if (deepProbeId || deepTrapId) {
      const target = validDevices.find((d) =>
        (d.deviceType === "rodent" && (d.displayId === deepProbeId || d.id === deepProbeId)) ||
        (d.deviceType === "smartprobe" && d.id === deepTrapId)
      );
      if (target) {
        mapRef.current.setCenter({ lat: target.latitude!, lng: target.longitude! });
        mapRef.current.setZoom(CLOSE_ZOOM);
        return;
      }
    }

    if (validDevices.length === 1) {
      setMapCenter({ lat: validDevices[0].latitude!, lng: validDevices[0].longitude! });
      setMapZoom(CLOSE_ZOOM);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      validDevices.forEach((d) => bounds.extend({ lat: d.latitude!, lng: d.longitude! }));
      mapRef.current.fitBounds(bounds, 60);
    }
  }, [validDevices, searchParams]);

  // ── Action message auto-dismiss ──────────────────────────────────────────────
  useEffect(() => {
    if (!actionMsg) return;
    const t = setTimeout(() => setActionMsg(null), 3500);
    return () => clearTimeout(t);
  }, [actionMsg]);

  const isLoading = rodentLoading || smartProbeLoading;

  // ── Refresh ──────────────────────────────────────────────────────────────────
  const handleRefresh = useCallback(async () => {
    if (!teamName || refreshing) return;
    setRefreshing(true);
    try {
      if (deviceFilter !== "smartprobe") await dispatch(getRodentProbes({ teamName })).unwrap();
      if (deviceFilter !== "rodent") await dispatch(getScrollInfoV2({ teamName, page: 1, pageSize: 100, filter: "id", ascend: true })).unwrap();
    } finally {
      setRefreshing(false);
    }
  }, [teamName, deviceFilter, dispatch, refreshing]);

  // ── Map click ────────────────────────────────────────────────────────────────
  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (markerPressedRef.current) {
      markerPressedRef.current = false;
      return;
    }
    if ((isEditMode || isAddMode) && e.latLng) {
      setNewMarkerLocation({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    } else {
      setSelectedDevice(null);
    }
  }, [isEditMode, isAddMode]);

  // ── Marker click ─────────────────────────────────────────────────────────────
  const handleMarkerClick = useCallback((device: NormalizedDevice) => {
    if (isEditMode || isAddMode) return;
    markerPressedRef.current = true;
    setSelectedDevice((prev) => prev?.uniqueId === device.uniqueId ? null : device);
    if (hasCoords(device)) {
      setMapCenter({ lat: device.latitude!, lng: device.longitude! });
      setMapZoom(CLOSE_ZOOM);
    }
  }, [isEditMode, isAddMode]);

  // ── Edit mode: Move device ────────────────────────────────────────────────────
  const handleMoveConfirm = useCallback(async () => {
    if (!selectedDevice || !newMarkerLocation) return;
    try {
      if (selectedDevice.deviceType === "rodent") {
        await dispatch(updateRodentProbeFields({
          probeId: selectedDevice.displayId,
          updates: { latitude: newMarkerLocation.lat, longitude: newMarkerLocation.lng },
        })).unwrap();
      } else {
        await dispatch(updateTrapCoordinates({
          trapId: selectedDevice.id,
          latitude: newMarkerLocation.lat,
          longitude: newMarkerLocation.lng,
        })).unwrap();
      }
      setActionMsg({ type: "success", text: `${selectedDevice.title} moved successfully.` });
      setMapCenter(newMarkerLocation);
    } catch {
      setActionMsg({ type: "error", text: "Failed to update location." });
    }
    setIsEditMode(false);
    setNewMarkerLocation(null);
    setSelectedDevice(null);
  }, [selectedDevice, newMarkerLocation, dispatch]);

  // ── Edit mode: Remove from map ───────────────────────────────────────────────
  const handleRemoveFromMap = useCallback(async () => {
    if (!selectedDevice) return;
    setShowRemoveConfirm(false);
    try {
      if (selectedDevice.deviceType === "rodent") {
        await dispatch(updateRodentProbeFields({
          probeId: selectedDevice.displayId,
          updates: { latitude: null, longitude: null },
        })).unwrap();
      } else {
        await dispatch(updateTrapCoordinates({
          trapId: selectedDevice.id,
          latitude: null,
          longitude: null,
        })).unwrap();
      }
      setActionMsg({ type: "success", text: `${selectedDevice.title} removed from map.` });
    } catch {
      setActionMsg({ type: "error", text: "Failed to remove from map." });
    }
    setIsEditMode(false);
    setNewMarkerLocation(null);
    setSelectedDevice(null);
  }, [selectedDevice, dispatch]);

  // ── Add mode: Place device ───────────────────────────────────────────────────
  const handlePlaceConfirm = useCallback(async () => {
    if (!selectedUnplacedDevice || !newMarkerLocation) return;
    try {
      if (selectedUnplacedDevice.deviceType === "rodent") {
        await dispatch(updateRodentProbeFields({
          probeId: selectedUnplacedDevice.displayId,
          updates: { latitude: newMarkerLocation.lat, longitude: newMarkerLocation.lng },
        })).unwrap();
      } else {
        await dispatch(updateTrapCoordinates({
          trapId: selectedUnplacedDevice.id,
          latitude: newMarkerLocation.lat,
          longitude: newMarkerLocation.lng,
        })).unwrap();
      }
      setActionMsg({ type: "success", text: `${selectedUnplacedDevice.title} placed on map.` });
      setMapCenter(newMarkerLocation);
    } catch {
      setActionMsg({ type: "error", text: "Failed to place device." });
    }
    setIsAddMode(false);
    setSelectedUnplacedDevice(null);
    setNewMarkerLocation(null);
  }, [selectedUnplacedDevice, newMarkerLocation, dispatch]);

  const cancelEdit = () => { setIsEditMode(false); setNewMarkerLocation(null); };
  const cancelAdd = () => { setIsAddMode(false); setSelectedUnplacedDevice(null); setNewMarkerLocation(null); };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation />

      <div className="flex" style={{ height: "calc(100vh - 4rem)" }}>
        {/* ── Left Panel ── */}
        <div className="w-72 flex-shrink-0 bg-white shadow-md flex flex-col overflow-y-auto z-10">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-gray-900">Device Map</h2>
              <button
                onClick={handleRefresh}
                disabled={refreshing || isLoading}
                className="p-1.5 rounded-full text-blue-500 hover:text-blue-700 disabled:text-gray-300 cursor-pointer"
                title="Refresh"
              >
                {refreshing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500" />
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
              </button>
            </div>

            {/* Filter tabs */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
              {(["all", "smartprobe", "rodent"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => { setDeviceFilter(f); setSelectedDevice(null); setIsEditMode(false); setIsAddMode(false); setNewMarkerLocation(null); }}
                  className={`flex-1 py-1.5 cursor-pointer transition-colors ${deviceFilter === f ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                >
                  {f === "all" ? "All" : f === "smartprobe" ? "SmartProbes" : "Rodent"}
                </button>
              ))}
            </div>
          </div>

          {/* Stats bar */}
          <div className="px-4 py-3 border-b border-gray-100 flex gap-3 text-xs text-gray-500">
            <span><span className="font-semibold text-gray-800">{validDevices.length}</span> on map</span>
            <span><span className="font-semibold text-gray-800">{unplacedDevices.length}</span> unplaced</span>
          </div>

          {/* Action message */}
          {actionMsg && (
            <div className={`mx-4 mt-3 px-3 py-2 rounded-lg text-xs font-medium ${actionMsg.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {actionMsg.text}
            </div>
          )}

          {/* Loading state */}
          {isLoading && !actionMsg && (
            <div className="flex justify-center items-center p-6">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
            </div>
          )}

          {/* No devices */}
          {!isLoading && allDevices.length === 0 && (
            <div className="p-4 text-center text-sm text-gray-400">
              No devices found for this team.
            </div>
          )}

          {/* Edit mode panel */}
          {isEditMode && selectedDevice && (
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Edit Placement</p>
              <p className="text-sm font-medium text-gray-800 mb-1">{selectedDevice.title}</p>
              <p className="text-xs text-gray-500 mb-3">
                {newMarkerLocation
                  ? "New location selected."
                  : "Tap the map to set a new location."}
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleMoveConfirm}
                  disabled={!newMarkerLocation}
                  className="w-full py-2 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 cursor-pointer"
                >
                  Confirm New Location
                </button>
                <button
                  onClick={() => setShowRemoveConfirm(true)}
                  className="w-full py-2 text-xs font-semibold rounded-lg bg-red-50 text-red-600 hover:bg-red-100 cursor-pointer"
                >
                  Remove from Map
                </button>
                <button
                  onClick={cancelEdit}
                  className="w-full py-2 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Add mode panel */}
          {isAddMode && (
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
                Place Device ({unplacedDevices.length} unplaced)
              </p>
              {unplacedDevices.length === 0 ? (
                <p className="text-xs text-gray-400">All devices are already placed.</p>
              ) : (
                <>
                  <div className="flex flex-col gap-1 mb-3 max-h-40 overflow-y-auto">
                    {unplacedDevices.map((d) => (
                      <button
                        key={d.uniqueId}
                        onClick={() => { setSelectedUnplacedDevice(d); setNewMarkerLocation(null); }}
                        className={`text-left px-3 py-2 rounded-lg text-xs border cursor-pointer transition-colors ${
                          selectedUnplacedDevice?.uniqueId === d.uniqueId
                            ? "bg-blue-50 border-blue-400 text-blue-700 font-semibold"
                            : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <div className="font-medium truncate">{d.title}</div>
                        <div className="text-gray-400 text-[10px] capitalize">{d.deviceType}</div>
                      </button>
                    ))}
                  </div>
                  {selectedUnplacedDevice && (
                    <p className="text-xs text-gray-500 mb-2">
                      {newMarkerLocation
                        ? "Location selected."
                        : "Tap the map to choose a location."}
                    </p>
                  )}
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={handlePlaceConfirm}
                      disabled={!selectedUnplacedDevice || !newMarkerLocation}
                      className="w-full py-2 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 cursor-pointer"
                    >
                      Confirm Location
                    </button>
                    <button
                      onClick={cancelAdd}
                      className="w-full py-2 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Selected device details (normal mode) */}
          {selectedDevice && !isEditMode && !isAddMode && (
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{
                    background: getMarkerColor(selectedDevice, yellow, lightRed, darkRed),
                  }}
                />
                <span className="text-sm font-bold text-gray-900 truncate">{selectedDevice.title}</span>
              </div>
              <p className="text-xs text-gray-500 mb-1">{selectedDevice.subtitle}</p>
              <p className="text-xs text-gray-400 capitalize mb-1">
                {selectedDevice.deviceType === "rodent" ? "Rodent Sensor" : "SmartProbe"}
                {" · ID: "}{selectedDevice.displayId}
              </p>
              {selectedDevice.deviceType === "rodent" && (
                <p className={`text-xs font-medium ${selectedDevice.isTriggered ? "text-red-500" : "text-green-600"}`}>
                  {selectedDevice.isTriggered ? "Motion Detected" : "No Motion Detected"}
                </p>
              )}
              {selectedDevice.deviceType === "smartprobe" && (
                <p className="text-xs text-gray-600">Insect Count: {selectedDevice.particleCount ?? 0}</p>
              )}
              <div className="mt-3 flex flex-col gap-2">
                <button
                  onClick={() => {
                    const path = selectedDevice.deviceType === "rodent" ? "/rodent-sensors" : "/dashboard";
                    navigate(path, { state: { selectId: selectedDevice.displayId } });
                  }}
                  className="w-full py-2 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 cursor-pointer"
                >
                  View on List
                </button>
                <button
                  onClick={() => { setIsEditMode(true); setIsAddMode(false); setNewMarkerLocation(null); }}
                  className="w-full py-2 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 cursor-pointer"
                >
                  Edit Placement
                </button>
                <button
                  onClick={() => setSelectedDevice(null)}
                  className="w-full py-2 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer"
                >
                  Deselect
                </button>
              </div>
            </div>
          )}

          {/* Place device button */}
          {!isEditMode && !isAddMode && unplacedDevices.length > 0 && (
            <div className="p-4">
              <button
                onClick={() => { setIsAddMode(true); setIsEditMode(false); setSelectedDevice(null); setNewMarkerLocation(null); }}
                className="w-full py-2.5 text-sm font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
              >
                Place Device ({unplacedDevices.length})
              </button>
            </div>
          )}
        </div>

        {/* ── Map ── */}
        <div className="flex-1 relative">
          {!isLoaded ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Loading map…</p>
              </div>
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={mapZoom}
              onClick={handleMapClick}
              onLoad={(map) => { mapRef.current = map; }}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true,
                zoomControl: true,
                styles: [
                  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
                  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
                  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
                ],
              }}
            >
              {/* Device markers */}
              {validDevices.map((device) => (
                <OverlayView
                  key={device.uniqueId}
                  position={{ lat: device.latitude!, lng: device.longitude! }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <CustomMarker
                    device={device}
                    isSelected={selectedDevice?.uniqueId === device.uniqueId}
                    color={getMarkerColor(device, yellow, lightRed, darkRed)}
                    onClick={() => handleMarkerClick(device)}
                  />
                </OverlayView>
              ))}

              {/* NEW marker (edit or add mode) */}
              {newMarkerLocation && (
                <OverlayView
                  position={newMarkerLocation}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <NewMarker
                    onClick={() => {
                      if (isEditMode) handleMoveConfirm();
                      else if (isAddMode) handlePlaceConfirm();
                    }}
                  />
                </OverlayView>
              )}
            </GoogleMap>
          )}

          {/* Mode overlay hint */}
          {(isEditMode || isAddMode) && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-blue-700 text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg pointer-events-none">
              {isEditMode ? "Tap map to set new location" : "Tap map to place device"}
            </div>
          )}
        </div>
      </div>

      {/* Remove-from-map confirmation modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-2">Remove from Map</h3>
            <p className="text-sm text-gray-600 mb-5">
              Remove <span className="font-semibold">{selectedDevice?.title}</span> from the map? The device will still exist in your account.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRemoveConfirm(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveFromMap}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-500 text-white hover:bg-red-600 cursor-pointer"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
